import { useCallback, useEffect, useRef } from 'react'
import { ApolloClient, useApolloClient } from '@apollo/client'

import usePusher from 'hooks/use-pusher'
import useIsomorphicLayoutEffect from 'hooks/use-isomorphic-layout-effect'
import bugsnagClient from 'clients/bugsnag'
import { sleep } from 'utilities/promises'
import { getEligibilityStatusAfterPusherEvent } from './queries.graphql'

/**
 * What the `eligibility-updated-<clientId>` event carries. Every Pusher channel
 * is public and `eligibility` is shared by every client, so the server sends
 * only the step and the retry count (node/utilities/client/eligibility-notify).
 */
export interface EligibilityPusherEvent {
  currentStep?: string | null
  retryCount?: number | null
}

/**
 * The client's latest eligibility row, read through GraphQL after the event.
 * The sign-up screens take the payer, copay and checked params from here.
 */
export interface EligibilityUpdate {
  id?: string | null
  currentStep: string
  retryCount: number | null
  policyStatus?: string | null
  serviceEligibilityStatus?: string | null
  policyMetadata: any
  serviceEligibilityMetadata: any
  eligibilityParams: any
}

/** Waits before each retry of a failed read: three retries over ~7 seconds. */
export const ELIGIBILITY_READ_RETRY_DELAYS_MS = [1000, 2000, 4000]

/**
 * Reads the latest eligibility row for a client after an eligibility event.
 * `no-cache` keeps this read from rewriting the row other queries on the page
 * are watching (their own polls and refetches own that).
 *
 * A failed read is retried after each delay in `retryDelaysMs`. If every
 * attempt fails, this returns null and reports a fixed message with the client
 * id. It never hands back a step without its policy details: a screen that
 * took a final step with no payer or copay would show the $25 default and stop
 * its own fallback (the survey poll, the countdown), which recover the full row.
 *
 * @param apolloClient - the page's Apollo client
 * @param clientId - the client the event was published for
 * @param retryDelaysMs - waits before each retry; tests pass shorter ones
 * @returns the latest eligibility row, or null when it could not be read
 */
export async function fetchEligibilityUpdate(
  apolloClient: ApolloClient<any>,
  clientId: number | string,
  retryDelaysMs: number[] = ELIGIBILITY_READ_RETRY_DELAYS_MS
): Promise<EligibilityUpdate | null> {
  for (let attempt = 0; attempt <= retryDelaysMs.length; attempt += 1) {
    if (attempt > 0) await sleep(retryDelaysMs[attempt - 1])
    try {
      const { data } = await apolloClient.query({
        query: getEligibilityStatusAfterPusherEvent,
        variables: { clientId: String(clientId) },
        fetchPolicy: 'no-cache',
      })
      const status = data?.client?.latestClientEligibilityStatus
      return status?.currentStep
        ? { ...status, retryCount: status.retryCount ?? null }
        : null
    } catch {
      // Retry; report once below if every attempt fails.
    }
  }

  // A fixed message and the client id only: the GraphQL error can quote
  // server text, and this flow handles insurance identity.
  bugsnagClient.notify(
    new Error('Eligibility status read after Pusher event failed'),
    bugsnagEvent => {
      bugsnagEvent.addMetadata('eligibility', { clientId })
    }
  )
  return null
}

/**
 * Subscribes to a client's eligibility events and hands the caller the
 * client's latest eligibility row, read through GraphQL, instead of the event
 * payload. The payload no longer carries the policy, service-eligibility or
 * checked params, so nothing here reads them from it.
 *
 * Only the newest event's read is applied. Reads run concurrently, so an older
 * read (a pending row) that finishes after a newer one (the final row) is
 * dropped rather than putting the screen back on its spinner. A read still in
 * flight when `clientId` changes or the component unmounts is dropped too.
 *
 * @param clientId - the client whose eligibility events to follow; no
 *   subscription while it is empty
 * @param onUpdate - called with each applied eligibility row
 * @param retryDelaysMs - waits before each retry of a failed read
 */
export default function useEligibilityUpdates(
  clientId: number | string | null | undefined,
  onUpdate: (update: EligibilityUpdate) => void,
  retryDelaysMs: number[] = ELIGIBILITY_READ_RETRY_DELAYS_MS
) {
  const apolloClient = useApolloClient()

  // The read is async, so call whichever handler is current when it returns.
  const onUpdateRef = useRef(onUpdate)
  useEffect(() => {
    onUpdateRef.current = onUpdate
  }, [onUpdate])

  // Numbers each event's read; only the newest one may apply its result.
  const latestReadRef = useRef(0)

  // A read still in flight when the client changes or the screen unmounts
  // belongs to the previous client: bump the number so it is dropped. A layout
  // effect, so the bump lands during the commit, before a read that settles
  // right after the render can apply.
  useIsomorphicLayoutEffect(() => {
    return () => {
      latestReadRef.current += 1
    }
  }, [clientId])

  /** Reads the latest row for one event and applies it if no newer event came. */
  const handleEvent = useCallback(async () => {
    if (!clientId) return
    latestReadRef.current += 1
    const read = latestReadRef.current
    const update = await fetchEligibilityUpdate(
      apolloClient,
      clientId,
      retryDelaysMs
    )
    if (update && read === latestReadRef.current) onUpdateRef.current(update)
    // retryDelaysMs is a constant in production; keep the handler stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apolloClient, clientId])

  usePusher(
    clientId ? 'eligibility' : null,
    clientId ? `eligibility-updated-${clientId}` : null,
    handleEvent
  )
}
